import {
  type AdProductSelectionConnected,
  AdProductSelectionConnectedCart,
  GetAdProductsForAdPostingDocument,
  type PurchaseContextUseCase,
} from '@seek/hirer-ad-products-selection';
import { useTranslations } from '@vocab/react';
import { Heading } from 'braid-design-system';
import { type ComponentProps, useCallback, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { TEALIUM_AD_TYPE } from 'src/app/shared/constants/dispatcherId';
import {
  SHOPPING_CART_KEY_CREATE_FLOW,
  SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY,
} from 'src/app/shared/constants/shoppingCart';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useProductSetFromCart } from 'src/app/shared/hooks/useProductSetFromCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getAdProductFromCart } from 'src/app/utils/getAdProductFromCart';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import tealiumDispatch from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateSelectedProductId } from 'src/app/v1/state/modules/formData/selectedProductId';
import { actionUpdateAddOnAdProductNames } from 'src/app/v1/state/modules/tealium/addOnAdProductNames';
import { actionResetHasCoverImage } from 'src/app/v1/state/modules/tealium/hasCoverImage';
import { actionResetHasLogo } from 'src/app/v1/state/modules/tealium/hasLogo';
import { actionUpdateProductPricing } from 'src/app/v1/state/modules/tealium/productPricing';
import { actionUpdateProductSetProductIds } from 'src/app/v1/state/modules/tealium/productSetProductIds';
import { AdSelectionErrorPage } from 'src/app/v1/views/components/AdSelectionWrapper/AdSelectionErrorPage';
import { useHubbleTrackEventHandler } from 'src/app/v1/views/hooks/useHubbleTrackEventHandler';
import type { CatalogueProductFeature, Maybe } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

type AdSelectionWrapperProps = {
  onProductSelect?: (adType: string) => void;
  purchaseContextUseCase?: PurchaseContextUseCase;
  hideDisclaimer?: boolean;
};

export const AdSelectionWrapper = ({
  onProductSelect,
  purchaseContextUseCase,
  hideDisclaimer,
}: AdSelectionWrapperProps) => {
  const {
    advertiserId,
    advertiserNation,
    draftId,
    jobLocationId,
    jobTitle,
    subClassificationId,
    currentSelectedProductId,
    jobLocationCountryCode,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.seekAdvertiserId,
      advertiserNation: state.session.hirerAccount.nation,
      draftId: state.session.draftId,
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      subClassificationId: state.session.seekSubClassificationId,
      currentSelectedProductId: state.formData.selectedProductId,
      jobLocationCountryCode: state.session.jobLocationCountry,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { getLocale } = useConfig();
  const locale = getLocale();
  const { sendHubbleTrackEvent } = useHubbleTrackEventHandler();

  const { productSetId: shoppingCartProductSetId, cartState } =
    useShoppingCart();
  const productSetFromCart = useProductSetFromCart({ draftId });

  const resetBrandingIfFeatureExists = useCallback(
    (features: Maybe<CatalogueProductFeature>[] | undefined) => {
      if (features) {
        const brandingFeature = features.find(
          (feature) => feature?.type === 'Branding',
        );
        if (!brandingFeature) {
          dispatch(actionResetHasCoverImage());
          dispatch(actionResetHasLogo());
        }
      }
    },
    [dispatch],
  );

  const adProductErrorHandler = useCallback(
    async (error: Error) => {
      log.error(
        'AdSelectionWrapper | HirerAdSelection | onError',
        {
          error,
          advertiserId,
          advertiserNation,
          jobAdDetails: {
            locationId: jobLocationId.toString(),
            roleTitle: jobTitle,
            subClassificationId,
          },
          draftId,
        },
        true,
      );
    },
    [
      advertiserId,
      advertiserNation,
      draftId,
      jobLocationId,
      jobTitle,
      subClassificationId,
    ],
  );

  const getAdProductsDocumentConstructVariables = useCallback(
    (productSetIdForInput: string) => ({
      input: {
        productSetId: productSetIdForInput,
        locale,
        purchaseContextUseCase,
      },
      draftId,
    }),
    [draftId, locale, purchaseContextUseCase],
  );

  const onTrackingCallback = useCallback(
    (eventData: unknown) => {
      const adSelectionEventData = eventData as TrackingEvent;
      if (
        adSelectionEventData.productPricing &&
        adSelectionEventData.productPricing.length > 0
      ) {
        dispatch(
          actionUpdateProductPricing(adSelectionEventData.productPricing),
        );
      }
      if (adSelectionEventData.productSetProductIds) {
        dispatch(
          actionUpdateProductSetProductIds(
            adSelectionEventData.productSetProductIds,
          ),
        );
      }
      getTrackingAdapter.sendLinkEvent(adSelectionEventData);
    },
    [dispatch],
  );

  const getAdProductSelectionConnectedProps = (
    productSetId: string,
    purchaseContextUseCaseForContext?: PurchaseContextUseCase,
  ): ComponentProps<typeof AdProductSelectionConnected> => ({
    onError: adProductErrorHandler,
    context: {
      hirer: { id: advertiserId },
      products: { productSetId },
      draft: draftId ? { id: draftId } : undefined,
      job: {
        location: {
          id: jobLocationId,
          country: { code: jobLocationCountryCode?.toUpperCase() },
        },
      },
      purchaseContextUseCase: purchaseContextUseCaseForContext,
    },
    selectedProductIds: currentSelectedProductId
      ? [currentSelectedProductId]
      : [],
    errorComponent: <AdSelectionErrorPage />,
    layout: {
      hideDisclaimer,
      prefixContent: {
        products: (
          <Heading level="3" component="h2">
            {t('select-ad-type-header')}
          </Heading>
        ),
        addOns: (
          <Heading level="3" component="h2">
            {t('select-add-on-header')}
          </Heading>
        ),
      },
    },
    onTrackingEvent: (payload) => {
      if (payload.tracking) {
        onTrackingCallback(payload.tracking);
      }
      if (payload.hubble) {
        sendHubbleTrackEvent(
          payload.hubble.eventName,
          payload.hubble.eventData,
        );
      }
    },
  });

  const shoppingCartProps = {
    getAdProductsDocument: GetAdProductsForAdPostingDocument,
    key: `${SHOPPING_CART_KEY_CREATE_FLOW}|${purchaseContextUseCase}`,
    subscriptionResponseKey: SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY,
  };

  const selectedProduct = useMemo(
    () =>
      getAdProductFromCart(
        cartState?.items || [],
        productSetFromCart?.items || [],
        'AdProduct',
      )?.[0],
    [cartState, productSetFromCart],
  );

  const selectedAddOns = useMemo(
    () =>
      getAdProductFromCart(
        cartState?.items || [],
        productSetFromCart?.items || [],
        'AdProductAddOn',
      ),
    [cartState, productSetFromCart],
  );

  useEffect(() => {
    if (selectedProduct) {
      dispatch(
        tealiumDispatch(
          TEALIUM_AD_TYPE,
          selectedProduct?.displayMetadata?.name || selectedProduct.name,
        ),
      );
      dispatch(actionUpdateSelectedProductId(selectedProduct.id));
      dispatch(actionUpdateProductSetProductIds([selectedProduct.id]));
      resetBrandingIfFeatureExists(selectedProduct.features);
    }

    if (selectedAddOns) {
      dispatch(
        actionUpdateAddOnAdProductNames(
          selectedAddOns.map((addOn) => addOn.name),
        ),
      );
    }
  }, [
    dispatch,
    onProductSelect,
    resetBrandingIfFeatureExists,
    selectedProduct,
    selectedAddOns,
  ]);

  if (!shoppingCartProductSetId) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<AdSelectionErrorPage />}>
      <AdProductSelectionConnectedCart
        {...getAdProductSelectionConnectedProps(
          shoppingCartProductSetId,
          purchaseContextUseCase,
        )}
        onProductSelect={(productId) => {
          onProductSelect?.(productId);
        }}
        shoppingCart={{
          ...shoppingCartProps,
          getAdProductsDocumentConstructVariables,
        }}
      />
    </ErrorBoundary>
  );
};
